import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import moment from 'moment';
import {
  Card,
  Form,
  Button,
} from 'react-bootstrap';
import { Typeahead } from 'react-bootstrap-typeahead';
import { LoadingAnimation } from '../../../common/components/Utilities';
import UserMeetingResource from '../../../common/resource/UserMeetingResource';
import LegacyUserResource from '../../../common/resource/LegacyUserResource';
import UserPill from '../../../common/components/UserPill';
import SafeHtml from '../../../common/components/SafeHtml';
import 'react-bootstrap-typeahead/css/Typeahead.css';

function OtherOptions({
  user, formData, leaderName, changeFormData,
}) {
  if (user.id !== user.manager.id) {
    return (
      <>
        <div className="custom-control custom-radio">
          <input className="custom-control-input" name="privacy" id="private_2" type="radio" value="2" onChange={changeFormData} checked={formData.privacy === '2'} />
          <label className="custom-control-label" htmlFor="private_2">
            Visible to
            {' '}
            {leaderName}
            {' '}
            and
            {' '}
            {user.name}
          </label>
        </div>
        <div className="custom-control custom-radio">
          <input className="custom-control-input" name="privacy" id="private_3" type="radio" value="3" onChange={changeFormData} checked={formData.privacy === '3'} />
          <label className="custom-control-label" htmlFor="private_3">
            Visible to
            {' '}
            {leaderName}
            {' '}
            Only
          </label>
        </div>
      </>
    );
  }
  return null;
}

function PrivacyOptions({
  user, formData, changeFormData,
}) {
  const leaderName = user.manager.id ? user.manager.name : 'Team Lead';

  return (
    <>
      <div className="custom-control custom-radio">
        <input className="custom-control-input" name="privacy" id="private_0" type="radio" value="0" onChange={changeFormData} checked={formData.privacy === '0'} />
        <label className="custom-control-label" htmlFor="private_0">
          Visible to SMT,
          {' '}
          {leaderName}
          {' '}
          and
          {' '}
          {user.name}
        </label>
      </div>
      <div className="custom-control custom-radio">
        <input className="custom-control-input" name="privacy" id="private_1" type="radio" value="1" onChange={changeFormData} checked={formData.privacy === '1'} />
        <label className="custom-control-label" htmlFor="private_1">
          Visible to SMT and
          {' '}
          {leaderName}
        </label>
      </div>

      <OtherOptions
        user={user}
        formData={formData}
        leaderName={leaderName}
        changeFormData={changeFormData}
      />
    </>
  );
}

function UserMeetings({ allUsers, activeUser, csrfToken }) {
  const [user, setUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [formParticipants, setFormParticipants] = useState([]);
  const [draftMeetingId, setDraftMeetingId] = useState(null);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftSavedTimerRef = useRef(null);
  const isPublishingRef = useRef(false);
  const notesRef = useRef(null);

  const defaultFormData = {
    subject: 'One to One',
    date_held: moment().format('YYYY-MM-DD'),
    notes: '',
    actions: '',
    privacy: '0',
  };
  const [formData, setFormData] = useState(defaultFormData);

  const showDraftSavedIndicator = useCallback(() => {
    setDraftSaved(true);
    if (draftSavedTimerRef.current) {
      clearTimeout(draftSavedTimerRef.current);
    }
    draftSavedTimerRef.current = setTimeout(() => setDraftSaved(false), 3000);
  }, []);

  // Clean up timer on unmount
  useEffect(() => () => {
    if (draftSavedTimerRef.current) {
      clearTimeout(draftSavedTimerRef.current);
    }
  }, []);

  const clearForm = useCallback(() => {
    setFormData({
      subject: 'One to One',
      date_held: moment().format('YYYY-MM-DD'),
      notes: '',
      actions: '',
      privacy: '0',
    });
    setFormParticipants([]);
    setDraftMeetingId(null);
    setDraftSaved(false);
  }, []);

  const editDraft = useCallback((draft) => {
    setFormData({
      subject: draft.subject || 'One to One',
      date_held: moment(draft.created_at).format('YYYY-MM-DD'),
      notes: draft.raw_notes || '',
      actions: draft.raw_actions || '',
      privacy: String(draft.privacy ?? '0'),
    });
    setDraftMeetingId(draft.id);
    setFormParticipants([]);
    if (notesRef.current) {
      notesRef.current.focus();
    }
  }, []);

  const saveAsDraft = useCallback(async () => {
    // Don't save if there's nothing to persist yet
    if (!formData.notes && !formData.actions) return;
    // Suppress blur-save while publish is in flight to prevent a race condition
    if (isPublishingRef.current) return;
    // Wait until the subject user has loaded
    if (!user) return;

    try {
      let updatedMeetings;
      if (draftMeetingId) {
        updatedMeetings = await UserMeetingResource.patch(user.id, draftMeetingId, {
          subject: formData.subject,
          date_held: formData.date_held,
          notes: formData.notes,
          actions: formData.actions,
          privacy: formData.privacy,
          is_draft: true,
        });
      } else {
        updatedMeetings = await UserMeetingResource.post(user.id, {
          subject: formData.subject,
          date_held: formData.date_held,
          notes: formData.notes,
          actions: formData.actions,
          privacy: formData.privacy,
          author_id: activeUser.id,
          is_draft: true,
        });
      }

      setMeetings(updatedMeetings);

      // Track the new draft ID after the first save so subsequent blurs PATCH instead of POST
      if (!draftMeetingId) {
        const ownDrafts = updatedMeetings
          .filter((m) => m.is_draft && m.author_id === activeUser.id)
          .sort((a, b) => b.id - a.id);
        if (ownDrafts.length > 0) {
          setDraftMeetingId(ownDrafts[0].id);
        }
      }

      showDraftSavedIndicator();
    } catch (exception) {
      console.error(exception);
    }
  }, [formData, draftMeetingId, user, activeUser, showDraftSavedIndicator]);

  const handleNotesBlur = useCallback(() => {
    saveAsDraft();
  }, [saveAsDraft]);

  const handleActionsBlur = useCallback(() => {
    saveAsDraft();
  }, [saveAsDraft]);

  const deleteMeeting = async (meetingId) => {
    setLoading(true);
    try {
      await UserMeetingResource.destroy(user.id, {
        meeting_id: meetingId,
        _token: csrfToken,
      });
      setMeetings(await UserMeetingResource.index(user.id));

      // If the deleted meeting was the active draft, clear the form
      if (meetingId === draftMeetingId) {
        clearForm();
      }
    } catch (exception) {
      console.error(exception);
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    let loadedUser = {};
    setLoading(true);
    const pageUrlUserId = window.location.pathname.split('/')[2];
    try {
      loadedUser = await LegacyUserResource.get(pageUrlUserId);
      setMeetings(await UserMeetingResource.index(loadedUser.id));
    } catch (exception) {
      console.error(exception);
    } finally {
      setLoading(false);
    }
    return loadedUser;
  };

  const changeFormData = (event) => {
    formData[event.target.name] = event.target.value;
    setFormData(JSON.parse(JSON.stringify(formData)));
  };

  useEffect(() => {
    let isMounted = true;
    loadData().then((loadedUserThen) => {
      if (isMounted) setUser(loadedUserThen);
    });
    return () => {
      isMounted = false;
    };
  }, []);

  const publishMeeting = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    isPublishingRef.current = true;
    setLoading(true);
    try {
      let updatedMeetings;
      if (draftMeetingId) {
        // Publish existing draft
        updatedMeetings = await UserMeetingResource.patch(user.id, draftMeetingId, {
          subject: formData.subject,
          date_held: formData.date_held,
          notes: formData.notes,
          actions: formData.actions,
          privacy: formData.privacy,
          participants: formParticipants.map((participant) => ({
            id: participant.id,
            name: participant.name,
          })),
          is_draft: false,
        });
      } else {
        // Create a new published meeting directly
        updatedMeetings = await UserMeetingResource.post(user.id, {
          _token: csrfToken,
          subject: formData.subject,
          date_held: formData.date_held,
          notes: formData.notes,
          actions: formData.actions,
          privacy: formData.privacy,
          author_id: activeUser.id,
          participants: formParticipants.map((participant) => ({
            id: participant.id,
            name: participant.name,
          })),
          is_draft: false,
        });
      }
      setMeetings(updatedMeetings);
      clearForm();
    } catch (exception) {
      console.error(exception);
    } finally {
      isPublishingRef.current = false;
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <Card>
        <LoadingAnimation loading={loading} inline />
      </Card>
    );
  }

  return (
    <>

      {(user.id === activeUser.id || user.manager.id === activeUser.id)
      && (
      <Card>
        <LoadingAnimation loading={loading} inline />
        <Card.Header>Add Meeting</Card.Header>
        <Card.Body>
          <Form onSubmit={publishMeeting} className="mb-4">
            <Form.Group>
              <Form.Label>Subject</Form.Label>
              <Form.Control as="input" name="subject" value={formData.subject} onChange={changeFormData} required />
            </Form.Group>
            <Form.Group>
              <Form.Label>Date Held</Form.Label>
              <Form.Control type="date" name="date_held" value={formData.date_held} onChange={changeFormData} />
            </Form.Group>
            <Form.Group>
              <Form.Label>Participants</Form.Label>
              <Typeahead
                id="basic-typeahead-multiple"
                labelKey="name"
                multiple
                onChange={setFormParticipants}
                options={allUsers}
                placeholder="Choose..."
                selected={formParticipants}
                allowNew
                newSelectionPrefix="Add external participant: "
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                ref={notesRef}
                rows={3}
                name="notes"
                value={formData.notes}
                onChange={changeFormData}
                onBlur={handleNotesBlur}
              />
            </Form.Group>
            <Form.Group>
              <Form.Label>Actions</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="actions"
                value={formData.actions}
                onChange={changeFormData}
                onBlur={handleActionsBlur}
              />
            </Form.Group>

            <PrivacyOptions user={user} formData={formData} changeFormData={changeFormData} />

            <div className="row align-items-center mt-4">
              <div className="col-md-6">
                {draftSaved && (
                  <span className="text-muted small">
                    <i className="fa fa-check mr-1" />
                    Draft saved
                  </span>
                )}
              </div>
              <div className="col-md-6 text-right">
                {draftMeetingId && (
                  <Button
                    variant="link"
                    size="sm"
                    className="mr-2 text-muted"
                    onClick={clearForm}
                  >
                    New meeting
                  </Button>
                )}
                <Button
                  type="submit"
                  className="btn btn-success"
                  disabled={loading || (!formData.notes && !formData.actions)}
                >
                  Publish
                </Button>
              </div>
            </div>
          </Form>
        </Card.Body>
      </Card>
      )}

      <MeetingList
        meetings={meetings}
        activeUser={activeUser}
        deleteMeeting={deleteMeeting}
        editDraft={editDraft}
        loading={loading}
      />
    </>
  );
}

function MeetingSingle({
  meeting, activeUser, deleteMeeting, editDraft, loading,
}) {
  let showLock;
  let showPrivacy;
  let showNotes;
  let showActions;
  let showDelete;
  let showParticipants;
  let meetingPrivate = false;

  if (meeting.privacy_statement.length) {
    if (meeting.isPrivate && (activeUser.id !== meeting.user_id)) {
      showLock = <i className="fa fa-lock mr-1" />;
      meetingPrivate = true;
    }
    showPrivacy = (
      <span className="ml-2 float-right small">
        {showLock}
        <i className="text-muted">{meeting.privacy_statement}</i>
      </span>
    );
  }

  if (meeting.display_notes && !meetingPrivate) {
    showNotes = (
      <div className="mt-3 mb-3">
        <h5>Notes</h5>
        <SafeHtml html={meeting.display_notes} />
      </div>
    );
  }

  if (meeting.display_actions && !meetingPrivate) {
    showActions = (
      <div className="mt-3 mb-3">
        <h5>Actions</h5>
        <SafeHtml html={meeting.display_actions} />
      </div>
    );
  }

  if (meeting.canDelete && !meetingPrivate) {
    showDelete = (
      <button type="button" className="btn btn-sm btn-outline-secondary float-right" onClick={() => deleteMeeting(meeting.id)} disabled={loading || ''}>
        <i className="fa fa-trash" />
        {' '}
        Delete
      </button>
    );
  }

  if (meeting.participants.length && !meetingPrivate) {
    const participants = meeting.participants.map((participant, index) => {
      if (participant.external_user_name) {
        return <span key={index} className="badge ml-1 badge-secondary" style={{ fontSize: '1em' }}>{participant.external_user_name}</span>;
      }
      if (participant.user_id > 0) {
        return <UserPill key={index} user={participant.user} />;
      }
      return null;
    });
    showParticipants = (
      <div className="mt-3 mb-3">
        <h5>Participants</h5>
        {participants}
      </div>
    );
  }

  const showEdit = !!meeting.is_draft && meeting.author_id === activeUser.id && !!editDraft;

  return (
    <div className="card mb-4">
      <div className="card-header">
        {showPrivacy}
        {!!meeting.is_draft && (
          <span className="badge badge-warning mr-2">Draft – only visible to you</span>
        )}
        <strong>{meeting.subject}</strong>
        {' '}
        -
        {' '}
        <UserPill user={meeting.author} />
        {' '}
        on
        {' '}
        {moment(meeting.created_at).format('MMM Do YYYY')}
      </div>
      <div className="card-body">
        {showParticipants}
        {showNotes}
        {showActions}
        {showEdit && (
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary float-right ml-2"
            onClick={() => editDraft(meeting)}
          >
            <i className="fa fa-edit" />
            {' '}
            Edit
          </button>
        )}
        {showDelete}
      </div>
    </div>
  );
}

function MeetingList({
  meetings, activeUser, deleteMeeting, editDraft, loading,
}) {
  let meetingsRender = [];
  if (!meetings.length) {
    meetingsRender = (
      <h3>No meetings yet.</h3>
    );
  } else {
    meetingsRender = meetings.map((meeting) => (
      <MeetingSingle
        key={meeting.id}
        meeting={meeting}
        activeUser={activeUser}
        deleteMeeting={deleteMeeting}
        editDraft={editDraft}
        loading={loading}
      />
    ));
  }
  return (
    <div className="mt-3">
      {meetingsRender}
    </div>
  );
}

export default UserMeetings;
